import { Grid } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { enqueueSnackbar } from 'notistack';

import SectionHeader from '../../components/SectionHeader';
import SearchToolbar from '../../components/SearchToolbar/SearchToolbar';
import useGlobalFilters from '../../hooks/useGlobalFilters';
import useUserAccess from '../../hooks/useUserAccess';
import { useRoles } from '../../hooks/useRoles';
import { getAccessValues } from '../../components/layout/RoutesManager/access';

import useSiniestros from './hooks/useSiniestros';
import SiniestrosFilters from './components/SiniestrosFilters/SiniestrosFilters';
import MenuTabs from './components/MenuTabs';
import SiniestrosTable from './components/SiniestrosTable';
import DescargarZipDialog from './components/DescargarZipDialog';

function SiniestrosIndex() {
    const { t } = useTranslation('siniestrosRoam');
    const access = useUserAccess();
    const { isSupervisorSIMP } = useRoles();
    const [isShowMoreFiltersOpen, setIsShowMoreFiltersOpen] = useState<boolean>(false);
    const FEATURES = getAccessValues(access);

    const {
        filters,
        setFilters,
        extraFilters,
        handleSubmitSearch,
        clearFilters,
        handleChangeExtraFilters
    } = useGlobalFilters({ ordenador: 'fechaDenuncia:desc' });

    const {
        activeTab,
        setActiveTab,
        siniestros,
        isLoading,
        totalCount,
        refetch,
        selectedIds,
        seleccionEstado,
        isSeleccionable,
        handleToggleSeleccion,
        handleSelectAll,
        handleClearSeleccion,
        handleGenerarTxtMasivo,
        isGenerandoTxt,
        handleDescargarZip,
        isLoadingZip,
        zipDialogOpen,
        zipArchivosAusentes,
        handleConfirmarDescargaZip,
        handleCancelarDescargaZip,
        handleExportarExcel,
        isExportandoExcel
    } = useSiniestros({ filters, extraFilters, isSupervisorSIMP });

    const handleTabChange = (value: any) => {
        setFilters((prevFilters: any) => ({
            ...prevFilters,
            skip: 0
        }));
        setActiveTab(value);
    };

    return (
        <>
            <SectionHeader>
                <SectionHeader.Title>{t('header.title')}</SectionHeader.Title>
                {/* El supervisor solo consulta: exporta a Excel pero no genera el TXT (SMP-460) */}
                {isSupervisorSIMP ? (
                    <SectionHeader.IconHeader
                        variant="outlined"
                        text="Exportar Excel"
                        showIcon={false}
                        loading={isExportandoExcel}
                        disabled={isExportandoExcel}
                        onClick={handleExportarExcel}
                    />
                ) : (
                    <SectionHeader.IconHeader
                        text="Generar TXT"
                        showIcon={false}
                        loading={isGenerandoTxt}
                        disabled={isGenerandoTxt}
                        onClick={() => {
                            if (!extraFilters?.clientId) {
                                enqueueSnackbar(
                                    'Para exportar, seleccioná un cliente en el filtro primero.',
                                    {
                                        variant: 'warning'
                                    }
                                );
                                return;
                            }
                            if (selectedIds.size === 0) {
                                enqueueSnackbar(
                                    'Seleccioná al menos un siniestro para generar el TXT',
                                    {
                                        variant: 'warning'
                                    }
                                );
                                return;
                            }
                            handleGenerarTxtMasivo();
                        }}
                    />
                )}
            </SectionHeader>

            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <SearchToolbar
                        onChange={() => {}}
                        onClick={handleSubmitSearch}
                        clearFilters={clearFilters}
                        showSearchInput={false}
                        showMoreFiltersButton={true}
                        isShowMoreFiltersOpen={isShowMoreFiltersOpen}
                        handleShowMoreFiltersPress={() =>
                            setIsShowMoreFiltersOpen((prevState: boolean) => !prevState)
                        }
                    >
                        <SiniestrosFilters
                            extraFilters={extraFilters}
                            handleChangeExtraFilters={handleChangeExtraFilters}
                            isShowMoreFiltersOpen={isShowMoreFiltersOpen}
                            activeTab={activeTab}
                            setActiveTab={setActiveTab}
                            conFiltroLugarOcurrencia
                        />
                    </SearchToolbar>
                </Grid>

                <Grid item xs={12}>
                    <MenuTabs
                        value={activeTab}
                        onChange={handleTabChange}
                        soloTodos={isSupervisorSIMP}
                    />
                </Grid>

                <Grid item xs={12}>
                    <SiniestrosTable
                        filters={filters}
                        setFilters={setFilters}
                        siniestros={siniestros}
                        isLoading={isLoading}
                        count={totalCount}
                        canViewDetail={FEATURES.CONSULTA_SINIESTRO_ROAM.hasAccess}
                        canCancel={FEATURES.CANCELACION_SINIESTRO_ROAM.hasAccess}
                        canCreateVisit={
                            FEATURES.GESTION_VISITA_DESDE_SINIESTRO_ROAM.hasAccess &&
                            FEATURES.ALTA_DE_VISITAS.hasAccess
                        }
                        activeTab={activeTab}
                        onRefresh={refetch}
                        t={t}
                        selectedIds={selectedIds}
                        seleccionEstado={seleccionEstado}
                        isSeleccionable={isSeleccionable}
                        handleToggleSeleccion={handleToggleSeleccion}
                        handleSelectAll={handleSelectAll}
                        handleClearSeleccion={handleClearSeleccion}
                        handleDescargarZip={isSupervisorSIMP ? undefined : handleDescargarZip}
                        isLoadingZip={isLoadingZip}
                    />
                    <DescargarZipDialog
                        open={zipDialogOpen}
                        archivosAusentes={zipArchivosAusentes}
                        onConfirmar={handleConfirmarDescargaZip}
                        onCancelar={handleCancelarDescargaZip}
                    />
                </Grid>
            </Grid>
        </>
    );
}

export default SiniestrosIndex;
